/**
 * API client for DSOK customer detail. Uses existing authenticated axios and
 * returns a normalized customer detail object for use in settings, ui-settings, etc.
 */
import { editDSOKDetails, getCustomer } from "app/services/api/management";
import { normalizeDsokCustomerDetail } from "./normalizeDsokCustomerDetail";

/**
 * Fetches customer detail by customerUrl. Returns normalized object with
 * customer core fields, dsokSettings (parsed), uiSettings, and other blocks.
 *
 * Device visibility: Business Partner (typ 2) and Service Partner (typ 7) are
 * intended to see all customer devices. The UI does not filter the device list
 * by role; the list comes from dsokSettings.customer.devices in this response.
 * If the backend returns a limited set for these roles, the backend must be
 * updated to return the full device set for typ 2 and 7 so the UI can display it.
 *
 * @param {string} customerUrl - Customer URL slug from route params
 * @returns {Promise<Object>} Normalized customer detail
 * @throws {Error} When the request fails or response is invalid
 */
export async function getDsokCustomerDetailByUrl(customerUrl) {
  if (!customerUrl || typeof customerUrl !== "string") {
    throw new Error("customerUrl is required");
  }

  let raw = await getCustomer(customerUrl);

  if (raw && typeof raw === "object" && raw.error) {
    throw new Error(raw.message || raw.error || "Failed to load customer data");
  }

  if (!raw || typeof raw !== "object") {
    throw new Error("Invalid customer response");
  }

  // Backend returns { response: { id, companyName, url, dsokSettings, ... } }. Unwrap if we got the full body.
  const inner = raw.response ?? raw.data;
  if (inner != null && typeof inner === "object" && (inner.id != null || inner.dsokSettings != null || inner.url != null)) {
    raw = inner;
  }

  // Treat as error when it looks like an error body (e.g. 401: { message: "..." })
  const hasCustomerData = raw.id != null || raw.dsokSettings != null || raw.url != null || (raw.customer != null && typeof raw.customer === "object");
  if (!hasCustomerData && raw.message != null) {
    throw new Error(raw.message || "Failed to load customer data");
  }

  return normalizeDsokCustomerDetail(raw);
}

/**
 * Updates customer dsokSettings on the backend (full replace).
 * Uses the same endpoint as DSOKDetails: PUT /customer/dsok with { customerUrl, ...dsokSettings }.
 *
 * @param {string} customerUrl - Customer URL slug
 * @param {Object} dsokSettings - Full dsokSettings object to persist
 * @returns {Promise<*>} Result from editDSOKDetails
 */
export async function updateDsokCustomerSettings(customerUrl, dsokSettings) {
  if (!customerUrl || typeof customerUrl !== "string") {
    throw new Error("customerUrl is required");
  }
  return editDSOKDetails({ customerUrl, ...dsokSettings });
}
